import type {
  UpdateWatchlistItemRequest,
  Watchlist,
  WatchlistItem,
  WatchlistDelta,
} from '@jam/shared';
import { authHeaders } from './auth-headers';

const API = '/api/watchlist';

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(await authHeaders()),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) throw new Error(`${res.status} ${await res.text().catch(() => '')}`);
  return (await res.json()) as T;
}

export function getWatchlist(): Promise<Watchlist> {
  return req<Watchlist>(API);
}

export function putWatchlist(items: WatchlistItem[]): Promise<Watchlist> {
  return req<Watchlist>(API, { method: 'PUT', body: JSON.stringify({ items }) });
}

export function addWatchlistEntity(entityName: string): Promise<Watchlist> {
  return req<Watchlist>(`${API}/items`, {
    method: 'POST',
    body: JSON.stringify({ entityName }),
  });
}

export function removeWatchlistEntity(entityName: string): Promise<Watchlist> {
  return req<Watchlist>(`${API}/items/${encodeURIComponent(entityName)}`, {
    method: 'DELETE',
  });
}

/** Patch one item's analyst-curated metadata (category / pinned /
 *  mutedUntil / note). Tri-state: undefined = leave alone, null = clear. */
export function updateWatchlistItem(
  entityName: string,
  patch: UpdateWatchlistItemRequest,
): Promise<Watchlist> {
  return req<Watchlist>(`${API}/items/${encodeURIComponent(entityName)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

// Re-export the shared muting predicate under the `isMuted` alias
// callers in this codebase expect. Logic lives in `@jam/shared`.
export { isWatchlistItemMuted as isMuted } from '@jam/shared';

export function stampVisited(): Promise<WatchlistDelta> {
  return req<WatchlistDelta>(`${API}/visited`, { method: 'POST' });
}

export interface RefreshIfStaleResult {
  refreshed: boolean;
  lastRefreshedAt: string | null;
  itemCount: number;
}

/**
 * Ask the server to diff the user's watchlist and emit new signals if the
 * last run was > 24h ago. No-op otherwise. Safe to call on every page
 * mount; the 24h gate lives server-side.
 */
export function refreshIfStale(): Promise<RefreshIfStaleResult> {
  return req<RefreshIfStaleResult>(`${API}/refresh-if-stale`, { method: 'POST' });
}
